// Repetition over time: one line per k, "nodes read at least k times".
//
// A "reading" is a SCHEDULED one — the first meeting with a node, and each
// review the schedule asked for. The server counts them (recommend.readings);
// the raw open log is bigger, because a node can be re-opened at any time. Those
// extra opens earn no rung on the ladder, so they must not move these lines.
//
// The series NEST — a node read five times is counted in the first five lines —
// so the lines can never cross, the vertical order is always 1 at the top and 10
// at the bottom, and the gap between two neighbouring lines is how many nodes
// are sitting at that repetition count. That nesting is what makes ten series
// readable at all: position carries identity, and colour only reinforces it.
//
// Each line gets its OWN hue, in a fixed order — the eight slots below are a
// validated set on this surface, not eight colours picked by eye. Eight is the
// ceiling: a ninth generated hue is indistinguishable from one already on the
// chart under colour-blindness, so k = 9 and k = 10 take the first two hues back
// with a dashed stroke instead — composite encoding, hue x pattern. Every line
// is also direct-labelled, named in the legend and read out on hover, so
// identity is never carried by colour alone.
import React, { useEffect, useMemo, useState } from "react";
import graphApi from "../services/graphApi";

// Fixed hue order: blue, orange, aqua, yellow, magenta, green, violet, red,
// stepped for a dark surface. The ORDER is the colour-blindness safety
// mechanism, not decoration — it clears every adjacent-pair gate against this
// card's background (worst adjacent CVD ΔE 8.4, normal-vision 19.3, all eight
// over 3:1 contrast). Reordering it breaks that, so add to the end or not at all.
const PALETTE = [
  "#3987e5",
  "#d95926",
  "#199e70",
  "#c98500",
  "#d55181",
  "#008300",
  "#9085e9",
  "#e66767",
];
const DASH = "5 3";

const seriesColor = (k) => PALETTE[(k - 1) % PALETTE.length];
const seriesDash = (k) => (k > PALETTE.length ? DASH : undefined);

// Plot box. Drawn in a fixed viewBox and scaled by CSS, so the layout maths
// below is in one coordinate system whatever width the card ends up with. The
// card spans a wide page, so the drawing is capped at its natural size (see
// `max-w` on the svg) — scaled past 1:1 the labels bloat and six days of data
// look like a billboard.
const W = 960;
const H = 330;
const PAD = { top: 14, right: 54, bottom: 30, left: 44 };
const PLOT = {
  x0: PAD.left,
  x1: W - PAD.right,
  y0: PAD.top,
  y1: H - PAD.bottom,
};

const WINDOWS = [
  { label: "30d", days: 30 },
  { label: "90d", days: 90 },
  { label: "all", days: 0 },
];

const AXIS = "#334155"; // slate-700
const GRID = "#1e293b"; // slate-800

function niceMax(value) {
  if (value <= 5) return Math.max(1, value);
  const power = 10 ** Math.floor(Math.log10(value));
  for (const step of [1, 1.5, 2, 3, 5, 10]) {
    if (value <= step * power) return step * power;
  }
  return 10 * power;
}

const formatDay = (iso) =>
  new Date(`${iso}T00:00:00`).toLocaleDateString("en-GB", {
    day: "numeric",
    month: "short",
  });

export default function ReadingProgress({ refreshKey = 0 }) {
  const [data, setData] = useState(null);
  const [status, setStatus] = useState("loading");
  const [days, setDays] = useState(0);
  const [scale, setScale] = useState("linear");
  const [showTable, setShowTable] = useState(false);
  const [hover, setHover] = useState(null);

  useEffect(() => {
    let live = true;
    setStatus("loading");
    graphApi
      .getProgress(days)
      .then((res) => {
        if (!live) return;
        setData(res);
        setStatus("ready");
      })
      .catch(() => live && setStatus("error"));
    return () => {
      live = false;
    };
  }, [days, refreshKey]);

  const chart = useMemo(() => {
    const points = data?.points || [];
    if (points.length === 0) return null;

    // Only the repetitions anything has actually reached. Drawing ten lines when
    // six of them are flat on the axis says "nothing is happening" ten times.
    const reached = [];
    for (let k = 1; k <= (data.max_k || 10); k += 1) {
      if (points.some((p) => (p.counts[k - 1] || 0) > 0)) reached.push(k);
    }
    if (reached.length === 0) return null;

    const top = niceMax(Math.max(...points.map((p) => p.counts[0] || 0)));
    const span = Math.max(1, points.length - 1);
    const x = (i) => PLOT.x0 + (i / span) * (PLOT.x1 - PLOT.x0);
    // Log makes the deep-repetition lines readable when the first line is an
    // order of magnitude above them; linear is the honest default, so the
    // choice is the reader's and the axis says which one is on.
    const y = (v) => {
      if (scale === "log") {
        const t = v <= 0 ? 0 : Math.log(v + 1) / Math.log(top + 1);
        return PLOT.y1 - t * (PLOT.y1 - PLOT.y0);
      }
      return PLOT.y1 - (v / top) * (PLOT.y1 - PLOT.y0);
    };

    // Deduplicated: a graph with two or three readings rounds several ticks onto
    // the same integer, which would stack gridlines and repeat React keys.
    const ticks = [
      ...new Set(
        scale === "log"
          ? [0, 1, 2, 5, 10, 20, 50, 100, 200, 500].filter((t) => t <= top)
          : Array.from({ length: 5 }, (_, i) => Math.round((top / 4) * i))
      ),
    ];

    const series = reached.map((k) => {
      const values = points.map((p) => p.counts[k - 1] || 0);
      const path = values.map((v, i) => `${i === 0 ? "M" : "L"}${x(i)} ${y(v)}`).join(" ");
      return {
        k,
        values,
        last: values[values.length - 1],
        // A single day has no line to draw; repeating the point makes the round
        // cap render it as a dot instead of nothing at all.
        d: values.length > 1 ? path : `${path} L${x(0)} ${y(values[0])}`,
      };
    });

    // Direct labels at the right end: the VALUE each line has reached, not its
    // k — the number of nodes is the thing being read off the chart, and the
    // legend already says which k a colour is. Skipped where two would collide,
    // which is also where the two lines are telling you the same number.
    let lastLabelY = -Infinity;
    const labels = [];
    for (const s of series) {
      const at = y(s.last);
      if (at - lastLabelY >= 14) {
        labels.push({ k: s.k, count: s.last, y: at });
        lastLabelY = at;
      }
    }

    // Day ticks: never more than six, always including the first and the last.
    const every = Math.max(1, Math.ceil(points.length / 6));
    const dayTicks = points
      .map((p, i) => ({ i, day: p.day }))
      .filter(({ i }) => i % every === 0 || i === points.length - 1);

    return { points, series, labels, ticks, dayTicks, top, x, y, reached };
  }, [data, scale]);

  // The pointer lands somewhere in the CSS box; the plot is in viewBox units.
  // Convert once, clamp to the plot, and snap to the nearest day — a crosshair
  // between two days would read a value that was never recorded.
  const track = (event) => {
    if (!chart) return;
    const box = event.currentTarget.getBoundingClientRect();
    if (!box.width) return;
    const inViewBox = ((event.clientX - box.left) / box.width) * W;
    const t = (inViewBox - PLOT.x0) / (PLOT.x1 - PLOT.x0);
    setHover(Math.round(Math.min(1, Math.max(0, t)) * (chart.points.length - 1)));
  };

  return (
    <section className="rounded-xl border border-slate-800 bg-slate-900/30 p-5">
      <div className="mb-1 flex flex-wrap items-baseline justify-between gap-3">
        <h2 className="text-lg font-semibold text-slate-200">Repetition over time</h2>
        <div className="flex items-center gap-2 text-xs">
          {WINDOWS.map((w) => (
            <button
              key={w.label}
              type="button"
              onClick={() => setDays(w.days)}
              className={`rounded-lg border px-2 py-1 transition ${
                days === w.days
                  ? "border-sky-500/40 bg-sky-500/10 text-sky-300"
                  : "border-slate-800 bg-slate-900/40 text-slate-400 hover:text-slate-200"
              }`}
            >
              {w.label}
            </button>
          ))}
          <button
            type="button"
            onClick={() => setScale(scale === "linear" ? "log" : "linear")}
            className="rounded-lg border border-slate-800 bg-slate-900/40 px-2 py-1 text-slate-400 transition hover:text-slate-200"
          >
            {scale === "linear" ? "linear" : "log"} scale
          </button>
          <button
            type="button"
            onClick={() => setShowTable(!showTable)}
            className="rounded-lg border border-slate-800 bg-slate-900/40 px-2 py-1 text-slate-400 transition hover:text-slate-200"
          >
            {showTable ? "chart" : "numbers"}
          </button>
        </div>
      </div>
      <p className="mb-4 text-xs text-slate-500">
        How many nodes had been read <strong className="font-medium text-slate-400">at
        least k times</strong> by the end of each day — counting the first reading
        and each review the schedule asked for, not every time a node was on
        screen. The lines nest, so the gap between two of them is how many nodes
        are sitting at that repetition count — and a line that climbs is spaced
        repetition actually happening.
      </p>

      {status === "loading" && (
        <p className="py-10 text-center text-sm text-slate-500">Loading…</p>
      )}
      {status === "error" && (
        <p className="py-10 text-center text-sm text-rose-400">
          Could not load the reading history.
        </p>
      )}
      {status === "ready" && !chart && (
        <p className="py-10 text-center text-sm text-slate-500">
          Nothing has been read yet — open a node and the first line starts here.
        </p>
      )}

      {/* The box is capped with the drawing so the hover read-out stays pinned
          to the plot rather than to the far edge of a wide card. */}
      {status === "ready" && chart && !showTable && (
        <div className="relative mx-auto max-w-[960px]">
          <svg
            viewBox={`0 0 ${W} ${H}`}
            className="block w-full"
            role="img"
            aria-label={`Nodes read at least k times, by day, for k = 1 to ${chart.reached.length}`}
            onMouseMove={track}
            onMouseLeave={() => setHover(null)}
          >
            {/* Grid and the value axis. Recessive: the lines are the subject. */}
            {chart.ticks.map((t) => (
              <g key={`y-${t}`}>
                <line
                  x1={PLOT.x0}
                  x2={PLOT.x1}
                  y1={chart.y(t)}
                  y2={chart.y(t)}
                  stroke={t === 0 ? AXIS : GRID}
                  strokeWidth="1"
                />
                <text
                  x={PLOT.x0 - 8}
                  y={chart.y(t) + 4}
                  textAnchor="end"
                  className="fill-slate-500"
                  fontSize="13"
                >
                  {t}
                </text>
              </g>
            ))}

            {chart.dayTicks.map(({ i, day }) => (
              <text
                key={`x-${day}`}
                x={chart.x(i)}
                y={H - 10}
                textAnchor={i === 0 ? "start" : i === chart.points.length - 1 ? "end" : "middle"}
                className="fill-slate-500"
                fontSize="13"
              >
                {formatDay(day)}
              </text>
            ))}

            {hover !== null && (
              <line
                x1={chart.x(hover)}
                x2={chart.x(hover)}
                y1={PLOT.y0}
                y2={PLOT.y1}
                stroke={AXIS}
                strokeWidth="1"
              />
            )}

            {chart.series.map((s) => (
              <path
                key={s.k}
                d={s.d}
                fill="none"
                stroke={seriesColor(s.k)}
                strokeDasharray={seriesDash(s.k)}
                strokeWidth="2"
                strokeLinejoin="round"
                strokeLinecap="round"
              />
            ))}

            {hover !== null &&
              chart.series.map((s) => (
                <circle
                  key={`dot-${s.k}`}
                  cx={chart.x(hover)}
                  cy={chart.y(s.values[hover])}
                  r="3.5"
                  fill={seriesColor(s.k)}
                  stroke="#020617"
                  strokeWidth="2"
                />
              ))}

            {chart.labels.map((l) => (
              <text
                key={`label-${l.k}`}
                x={PLOT.x1 + 8}
                y={l.y + 4}
                className="fill-slate-400"
                fontSize="13"
              >
                {l.count}
              </text>
            ))}
          </svg>

          {hover !== null && (
            <div className="pointer-events-none absolute right-2 top-2 rounded-lg border border-slate-700 bg-slate-950/95 px-3 py-2 text-xs shadow-lg">
              <div className="mb-1 font-medium text-slate-300">
                {formatDay(chart.points[hover].day)}
              </div>
              <ul className="space-y-0.5">
                {chart.series.map((s) => (
                  <li key={`t-${s.k}`} className="flex items-center gap-2">
                    <span
                      className="inline-block h-0.5 w-4"
                      style={{
                        backgroundColor: seriesColor(s.k),
                        opacity: seriesDash(s.k) ? 0.6 : 1,
                      }}
                    />
                    <span className="text-slate-500">≥{s.k}</span>
                    <span className="ml-auto tabular-nums text-slate-300">
                      {s.values[hover]}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}

      {status === "ready" && chart && showTable && (
        <div className="max-h-80 overflow-auto rounded-lg border border-slate-800">
          <table className="w-full text-xs">
            <thead className="sticky top-0 bg-slate-900 text-slate-400">
              <tr>
                <th className="px-3 py-2 text-left font-medium">day</th>
                {chart.series.map((s) => (
                  <th key={`h-${s.k}`} className="px-3 py-2 text-right font-medium">
                    ≥{s.k}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-800/70">
              {[...chart.points].reverse().map((p, i) => (
                <tr key={p.day} className={i === 0 ? "text-slate-200" : "text-slate-400"}>
                  <td className="px-3 py-1.5">{formatDay(p.day)}</td>
                  {chart.series.map((s) => (
                    <td key={`c-${p.day}-${s.k}`} className="px-3 py-1.5 text-right tabular-nums">
                      {p.counts[s.k - 1] || 0}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {status === "ready" && chart && (
        <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1 text-[11px] text-slate-500">
          {chart.series.map((s) => (
            <span key={`leg-${s.k}`} className="flex items-center gap-1.5">
              <svg width="18" height="6" aria-hidden="true">
                <line
                  x1="0"
                  y1="3"
                  x2="18"
                  y2="3"
                  stroke={seriesColor(s.k)}
                  strokeDasharray={seriesDash(s.k)}
                  strokeWidth="2"
                />
              </svg>
              read ≥{s.k}
            </span>
          ))}
        </div>
      )}
    </section>
  );
}
